import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Key figures of a page or period, in the dashboard header's language:
 * small muted label, large tabular value, an optional secondary line.
 * Sits on the right of a PageHeader (`aside`) or on its own.
 */
export function SummaryFigures({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <dl className={cn("flex flex-wrap gap-x-10 gap-y-3", className)}>{children}</dl>;
}

const TONES = {
  default: "text-foreground",
  positive: "text-emerald-600",
  negative: "text-destructive",
  warning: "text-amber-600 dark:text-amber-500",
  personal: "text-[hsl(var(--personal-primary))]",
} as const;

export type SummaryTone = keyof typeof TONES;

interface SummaryFigureProps {
  label: ReactNode;
  value: ReactNode;
  /** Secondary line under the value: other currencies, a count, a hint. */
  sub?: ReactNode;
  tone?: SummaryTone;
}

export function SummaryFigure({ label, value, sub, tone = "default" }: SummaryFigureProps) {
  return (
    <div className="min-w-0">
      <dt className="mb-1 text-xs font-medium text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "whitespace-nowrap text-[22px] font-semibold tabular-nums leading-tight tracking-tight 2xl:text-[26px]",
          TONES[tone]
        )}
      >
        {value}
      </dd>
      {sub && <dd className="mt-0.5 text-[13px] tabular-nums text-muted-foreground">{sub}</dd>}
    </div>
  );
}
